import * as React from 'react';
import { useState, useEffect } from 'react';
import { AlertVariant, Button, Grid, ProgressBar } from 'react-unity';
import { useNavigate } from 'react-router-dom';
import AlertModal from '../common/modals/AlertModal';
import AWSReduceAccount from '../../models/entities/AWSReduceAccount';
import AWSAPermissionInfo from '../../models/entities/AWSPermissionInfo';
import AWSSSOService from '../../services/AWSSSOService';
import GroupLookup from '../common/form-controls/Lookups/GroupLookup';
import ReactButton from '../common/ReactButton';
import RequestFormWrapper from '../common/wrappers/RequestFormWrapper';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import SelectField from '../common/form-controls/SelectField';
import {ToggleField} from '../common/';
import Group from '../../models/Group';
import AwsSSOHomeTable from './AwsSSOHomeTable';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import AWSPermissionRequest from '../../models/entities/AWSPermissionRequest';
import ConfirmModal from '../common/modals/ConfirmModal';

interface Option {
	value: string;
	text: string;
}

const AwsSsoHome = () => {
	const [areMyRequestsLoading, setAreMyRequestsLoading] = useState<boolean>(false);
	const [progressLabel, setProgressLabel] = useState<string>('Loading...');

	const [accountOptions, setAccountOptions] = useState<Option[]>([]);
	const [optionSelec, setOptionSelec] = useState<Option>({ value: null, text: '' });
	const [accountList, setAccountList] = useState<AWSReduceAccount[]>([]);
	const [account, setAccount] = useState<AWSReduceAccount>({ number: '', name: '', arn: '' });

	const [group, setGroup] = useState<Group>();

	const [permissionType, setPermissionType] = useState<string>('0');

	const [listPermissions, setListPermissions] = useState<AWSAPermissionInfo[]>([]);
	const [assignedPermissions, setAssignedPermissions] = useState<AWSAPermissionInfo[]>([]);
	const [permissionsSelected, setPermissionsSelected] = useState<AWSAPermissionInfo>();
	const [permissionLoading, setPermissionLoading] = useState<boolean>(false);
	const [confirmModalVisible, setConfirmModalVisible] = useState<boolean>(false);

	const [operationInProgress, setOperationInProgress] = useState<boolean>(false);
	const [refresh, setRefresh] = useState<boolean>(false);

	const [alert, setAlert] = useState<AlertBanner>({
		visible: false,
		variant: 'default',
		text: '',
	});

	const navigate = useNavigate();
	const awsSSOService = new AWSSSOService();

	const endAndRedirect = () => {
		navigate('/');
	};

	const isValid = () => {
		return account && permissionsSelected && group;
	};

	const retrieveAwsAccounts = async () => {
		setAreMyRequestsLoading(true);
		setProgressLabel('Retrieving AWS Accounts...');
		try {
			const response = await awsSSOService.getAllAWSByUser();
			const accountArray = response.map((element) => ({
				number: element.number,
				name: element.name,
				arn: element.arn,
			}));
			setAccountList(accountArray);
			let options = [];
			response.map((element) => {
				options.push({ value: element.number, text: element.name });
			});
			setAccountOptions(options);
		} catch (error) {
			setAlert({
				visible: true,
				variant: 'error',
				text: error?.response?.data?.message ?? 'Something went wrong. Please try again later...',
			});
			setTimeout(() => endAndRedirect(), 1000);
		} finally {
			setAreMyRequestsLoading(false);
		}
	};

	const resetForm = () => {
		setAccountOptions([]);
		setOptionSelec({ value: null, text: '' });
		setAccountList([]);
		setAccount({ number: '', name: '', arn: '' });
		setGroup(null);
		setPermissionType('0');
		setListPermissions([]);
		setAssignedPermissions([]);
		setPermissionsSelected(null);
		setPermissionLoading(false);
		setConfirmModalVisible(false);
	};

	const handleAccountChange = (event) => {
		setOperationInProgress(true);
		setPermissionLoading(true);
		const opSel = accountOptions[event.target.selectedIndex - 1];
		setOptionSelec(opSel);
		const accSel = accountList[event.target.selectedIndex - 1];
		setAccount(accSel);
		setOperationInProgress(false);
		setPermissions(accSel);
	};

	const manageChangeGroup = (event: any) => setGroup(event);

	const manageChangeRequestType = (event) => {
		setPermissionLoading(true);
		if (event.target.value != permissionType) setPermissionType(event.target.value);
		setPermissionsSelected(null);
		setPermissionLoading(false);
	};

	const scopePermissionOptions = () => {
		if (permissionType == '0') {
			return (
				listPermissions.map((element) => {
					return { value: element.permissionSetArn, text: element.name };
				}) || []
			);
		} else {
			return (
				assignedPermissions.map((element) => {
					return { value: element.associationID, text: element.permissionSetName };
				}) || []
			);
		}
	};

	const handleOnChangePermission = (e) => {
		try {
			setPermissionsSelected(e.target.value);
		} catch (error) {
			setAlert({
				visible: true,
				variant: 'error',
				text: error?.response?.data?.message ?? 'Something went wrong. Please try again later...',
			});
		}
	};

	const setPermissions = async (acc?: AWSReduceAccount) => {
		setPermissionLoading(true);
		try {
			if (acc.number == '') {
				setPermissionLoading(false);
				return;
			}
			const [listPermissions, assignedPermissions] = await Promise.all([
				awsSSOService.getListOfPermissions(acc.number),
				awsSSOService.getAssignedPermissions(acc.number),
			]);
			setListPermissions(listPermissions);
			setAssignedPermissions(assignedPermissions);
		} catch (error) {
			if (error.code == 'ERR_CANCELED') {
				console.log('Request was cancelled');
				return;
			}
			setAlert({
				visible: true,
				variant: 'error',
				text: error?.response?.data?.message ?? 'Something went wrong. Please try again later...',
			});
			setTimeout(() => endAndRedirect(), 1000);
		} finally {
			setPermissionLoading(false);
		}
	};

	const handleSubmit = async () => {
		setOperationInProgress(true);
		setProgressLabel('Submitting Request...');
		try {
			const permission = {
				accountNumber: account.number,
				permissionSetARN: permissionsSelected,
				principalId: group.id,
				principalName: group.displayName,
				principalType: 'GROUP',
				status: 'Create',
			};
			await awsSSOService.submitSetPrincipalsAndPermission(new AWSPermissionRequest(permission));
			setAlert({ visible: true, variant: 'success', text: 'Request submitted successfully' });
		} catch (error) {
			setAlert({
				visible: true,
				variant: 'error',
				text: error?.response?.data?.message ?? 'Something went wrong. Please try again later...',
			});
		} finally {
			setOperationInProgress(false);
			setRefresh(!refresh);
			resetForm();
		}
	};

	const handleDeletePermission = async () => {
		setConfirmModalVisible(false);
		setOperationInProgress(true);
		setProgressLabel('Submitting Request...');
		try {
			const permission = {
				associationID: permissionsSelected.associationID,
				principalId: permissionsSelected.principal?.externalID,
			};
			await awsSSOService.submitUnsetPrincipalsAndPermission(new AWSPermissionRequest(permission));
			setAlert({ visible: true, variant: 'success', text: 'Request submitted successfully' });
		} catch (error) {
			setAlert({
				visible: true,
				variant: 'error',
				text: error?.response?.data?.message ?? 'Something went wrong. Please try again later...',
			});
		} finally {
			setOperationInProgress(false);
			setTimeout(() => {
				setPermissions(account);
			}, 3000);
		}
	};

	const handleRefresh = () => {
		setPermissions(account);
	};

	useEffect(() => {
		retrieveAwsAccounts();
	}, [refresh]);

	return (
		<div>
			<SectionHomeWrapper title={`AWS SSO`}>
				{areMyRequestsLoading && (
					<ProgressBar className="em-u-margin-top-half" indeterminate hideValueLabel label={progressLabel} />
				)}
			</SectionHomeWrapper>
			{!areMyRequestsLoading && (
				<>
					<RequestFormWrapper title={'New AWS SSO Request'} loading={areMyRequestsLoading}>
						{operationInProgress && (
							<ProgressBar
								className="em-u-margin-top-half"
								indeterminate
								hideValueLabel
								label={progressLabel}
							/>
						)}
						<Grid variant="halves">
							<Grid.Item>
								<SelectField
									label={'Select an AWS Account *'}
									options={accountOptions}
									className="full-width"
									value={optionSelec.value}
									disabled={operationInProgress || permissionLoading}
									onChange={(event) => handleAccountChange(event)}
								/>
							</Grid.Item>
						</Grid>
						<Grid variant="halves">
							<Grid.Item>
								<ToggleField
									id="permission-type"
									label="Request Type *"
									onChange={(event) => manageChangeRequestType(event)}
									value={permissionType}
									disabled={permissionLoading || operationInProgress}
									options={[
										{ value: '0', text: 'Add' },
										{ value: '1', text: 'Remove' },
									]}
								/>
							</Grid.Item>
						</Grid>
						{permissionType == '1' ? (
							<>
								{permissionLoading ? (
									<ProgressBar indeterminate hideValueLabel label={'Retrieving Permissions...'} />
								) : (
									<AwsSSOHomeTable
										assignedPermissions={assignedPermissions}
										setConfirmModalVisible={setConfirmModalVisible}
										setPermissionsSelected={setPermissionsSelected}
										operationInProgress={operationInProgress || permissionLoading}
										handleRefresh={handleRefresh}
									/>
								)}
							</>
						) : (
							<>
								<Grid variant="halves">
									<Grid.Item>
										<GroupLookup
											value={group?.displayName}
											onChange={(event) => manageChangeGroup(event)}
											label="Entra ID Group *"
											placeholder="Group Name"
											note="This must be an existing group in Microsoft Entra ID"
											disabled={operationInProgress || permissionLoading}
										/>
									</Grid.Item>
								</Grid>
								<Grid variant="halves">
									<Grid.Item>
										{permissionLoading ? (
											<ProgressBar
												indeterminate
												hideValueLabel
												label={'Retrieving Permissions...'}
											/>
										) : listPermissions.length > 0 ? (
											<SelectField
												label={'Select permission to be added *'}
												value={permissionsSelected}
												options={scopePermissionOptions()}
												className="full-width"
												onChange={handleOnChangePermission}
												disabled={operationInProgress || permissionLoading}
											/>
										) : (
											<strong>No permissions available</strong>
										)}
									</Grid.Item>
								</Grid>
								<Grid variant="4-up">
									<Grid.Item>
										<Button
											variant="secondary"
											onClick={() => endAndRedirect()}
											disabled={operationInProgress || permissionLoading}
										>
											Cancel Request
										</Button>
									</Grid.Item>
									<Grid.Item />
									<Grid.Item>
										<ReactButton
											variant="primary"
											disabled={!isValid() || operationInProgress || permissionLoading}
											isLoading={operationInProgress || permissionLoading}
											handleUpdateSubmit={handleSubmit}
											loadingVariant="secondary"
											name="Submit Request"
										/>
									</Grid.Item>
								</Grid>
							</>
						)}
					</RequestFormWrapper>
				</>
			)}
			<AlertModal
				visible={alert.visible}
				willTimeout={false}
				text={alert.text}
				variant={alert.variant}
				onClose={() => {
					setAlert({ visible: false, variant: 'default', text: '' });
				}}
			/>
			<ConfirmModal
				visible={confirmModalVisible}
				title={`Delete Permission ${permissionsSelected?.permissionSetName}`}
				question="Are you sure you want to delete this permission? This action cannot be undone."
				confirmButton={{
					label: 'Delete Permission',
					props: {
						variant: 'primary',
						color: 'negative',
					},
				}}
				onConfirm={() => handleDeletePermission()}
				onCancel={() => setConfirmModalVisible(false)}
			/>
		</div>
	);
};

export default AwsSsoHome;
